import React, { useState, useEffect } from "react";
import { apiUrl } from "../utils/api";
import {
    Dialog,
    DialogSurface,
    DialogTitle,
    DialogBody,
    DialogActions,
    Button,
    makeStyles,
    Input,
    Dropdown,
    Option,
    tokens,
    Text,
    Spinner,
    Table,
    TableHeader,
    TableRow,
    TableHeaderCell,
    TableBody,
    TableCell,
} from "@fluentui/react-components";
import { AddRegular, DeleteRegular, Search24Regular, ChevronRight20Regular, Person24Regular } from "@fluentui/react-icons";

export type CompanyMember = {
    id: number;
    lastname: string;
    firstname: string;
    role: string;
    function: string;
    shares: string;
};

type Props = {
    open: boolean;
    onClose: () => void;
    onConfirm: (members: CompanyMember[]) => void;
    initialMembers?: CompanyMember[];
};

const useStyles = makeStyles({
    surface: {
        width: "min(1080px, 95vw)",
        maxWidth: "1080px",
    },
    content: {
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        marginTop: "16px",
        overflowY: "auto",
        maxHeight: "60vh",
    },
    inputGroup: {
        display: "grid",
        gridTemplateColumns: "1.5fr 1.5fr 1.5fr 1.5fr 90px 40px",
        gap: "12px",
        alignItems: "end",
    },
    field: {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
    },
    table: {
        marginTop: "24px",
    },
    searchContainer: {
        marginBottom: "24px",
        padding: "16px",
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    searchResults: {
        maxHeight: "200px",
        overflowY: "auto",
        marginTop: "8px",
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
        boxShadow: tokens.shadow4,
    },
    searchResultItem: {
        padding: "8px 12px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        "&:hover": {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
        "&:last-child": {
            borderBottom: "none",
        },
    },
});

export const CompanyMemberDialog: React.FC<Props> = ({
    open,
    onClose,
    onConfirm,
    initialMembers,
}) => {
    const s = useStyles();
    const [members, setMembers] = useState<CompanyMember[]>([]);

    useEffect(() => {
        if (open) {
            setMembers(initialMembers || []);
        } else {
            setMembers([]);
            setLastname("");
            setFirstname("");
            setRole("");
            setFunc("");
            setShares("");
        }
    }, [open, initialMembers]);

    // Current Input State
    const [lastname, setLastname] = useState("");
    const [firstname, setFirstname] = useState("");
    const [role, setRole] = useState("");
    const [func, setFunc] = useState("");
    const [shares, setShares] = useState("");

    // Search State
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    const handleSearch = async () => {
        if (!searchQuery) return;
        setIsSearching(true);
        try {
            const res = await fetch(apiUrl("contact", { search: searchQuery, limit: 50 }));
            const data = await res.json();
            setSearchResults(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Failed to search contacts", err);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const selectContact = (contact: any) => {
        setLastname(contact.lastname || "");
        setFirstname(contact.vorname || contact.firstName || "");
        // Other fields like role, func, shares need typically manual input or aren't on the general contact
        // If contact has company related info we could try to map it, but usually not.

        // Reset search
        setSearchQuery("");
        setSearchResults([]);
    };

    const handleAdd = () => {
        if (!lastname || !firstname || !role || !func) return;

        const newMember: CompanyMember = {
            id: Date.now(),
            lastname,
            firstname,
            role,
            function: func,
            shares,
        };

        setMembers((prev) => [...prev, newMember]);

        // Reset fields
        setLastname("");
        setFirstname("");
        setRole("");
        setFunc("");
        setShares("");
    };

    const handleDelete = (id: number) => {
        setMembers((prev) => prev.filter((m) => m.id !== id));
    };

    const roleOptions = ["Mitarbeiter", "Inhaber", "Externer Berater"];
    const functionOptions = ["Ansprechpartner"];

    // Sort: Members with shares first
    const sortedMembers = [...members].sort((a, b) => {
        const aShares = parseFloat(a.shares) || 0;
        const bShares = parseFloat(b.shares) || 0;

        // Logic: if a has shares and b doesn't, a comes first.
        // If both have shares, maybe descending?
        const aHasShares = aShares > 0;
        const bHasShares = bShares > 0;

        if (aHasShares && !bHasShares) return -1;
        if (!aHasShares && bHasShares) return 1;
        if (aHasShares && bHasShares) return bShares - aShares; // Descending shares
        return 0;
    });

    return (
        <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
            <DialogSurface className={s.surface}>
                <DialogTitle>Firmenzugehörige erfassen</DialogTitle>
                <DialogBody className={s.content}>
                    {/* Search Section */}
                    <div className={s.searchContainer}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                            Bestehenden Kontakt auswählen (optional)
                        </label>
                        <div style={{ display: "flex", gap: "8px" }}>
                            <Input
                                value={searchQuery}
                                onChange={(_, d) => setSearchQuery(d.value)}
                                placeholder="Kontakt suchen..."
                                contentAfter={
                                    isSearching ? <Spinner size="tiny" /> : <Search24Regular onClick={handleSearch} style={{ cursor: 'pointer' }} />
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSearch();
                                }}
                                style={{ flex: 1 }}
                            />
                            <Button onClick={handleSearch} disabled={isSearching}>Suchen</Button>
                        </div>
                        {searchResults.length > 0 && (
                            <div className={s.searchResults}>
                                {searchResults.map((result) => (
                                    <div
                                        key={result.id}
                                        className={s.searchResultItem}
                                        onClick={() => selectContact(result)}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Person24Regular />
                                            <Text>
                                                {result.lastname}, {result.vorname ?? result.firstName}
                                                {result.companyName && <span style={{ color: tokens.colorNeutralForeground3, fontSize: '12px', marginLeft: '8px' }}>({result.companyName})</span>}
                                            </Text>
                                        </div>
                                        <ChevronRight20Regular />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        {/* Input Row */}
                        <div className={s.inputGroup}>
                            <div className={s.field}>
                                <label>Nachname</label>
                                <Input
                                    value={lastname}
                                    onChange={(e, d) => setLastname(d.value)}
                                    placeholder="Mustermann"
                                />
                            </div>
                            <div className={s.field}>
                                <label>Vorname</label>
                                <Input
                                    value={firstname}
                                    onChange={(e, d) => setFirstname(d.value)}
                                    placeholder="Max"
                                />
                            </div>
                            <div className={s.field}>
                                <label>Rolle</label>
                                <Dropdown
                                    value={role}
                                    onOptionSelect={(e, d) => setRole(d.optionValue || "")}
                                    placeholder="Rolle wählen"
                                >
                                    {roleOptions.map((r) => (
                                        <Option key={r} value={r}>
                                            {r}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                            <div className={s.field}>
                                <label>Funktion</label>
                                <Dropdown
                                    value={func}
                                    onOptionSelect={(e, d) => setFunc(d.optionValue || "")}
                                    placeholder="Funktion wählen"
                                >
                                    {functionOptions.map((f) => (
                                        <Option key={f} value={f}>
                                            {f}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                            <div className={s.field}>
                                <label>Anteile (%)</label>
                                <Input
                                    value={shares}
                                    onChange={(e, d) => setShares(d.value)}
                                    placeholder="50"
                                    type="number"
                                />
                            </div>
                            <Button
                                icon={<AddRegular />}
                                appearance="primary"
                                onClick={handleAdd}
                                disabled={!lastname || !firstname || !role || !func}
                                style={{ marginBottom: "2px" }}
                            />
                        </div>
                    </div>
                </DialogBody>
                <DialogActions>
                    <Button onClick={onClose}>Abbrechen</Button>
                    <Button
                        appearance="primary"
                        onClick={() => onConfirm(members)}
                        disabled={members.length === 0}
                    >
                        Übernehmen
                    </Button>
                </DialogActions>
            </DialogSurface>
        </Dialog>
    );
};
